import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LicenseBadge } from '@/components/fintech/LicenseBadge';
import { toast } from 'sonner';

export default function LoginPage() {
  const { t } = useLanguage();
  const { loginWithToken } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post('/auth/login', { email, password });
      if (r.data.requires_verification) {
        toast.info('Please verify your email first');
        nav(`/verify-email?email=${encodeURIComponent(email)}${r.data.dev_code ? `&dev_code=${r.data.dev_code}` : ''}`);
        return;
      }
      loginWithToken(r.data.token, r.data.user);
      toast.success('Signed in');
      nav('/wallet');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center py-16 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <Link to="/" className="inline-block">
            <img src="/logo.png" alt="Concorde Financial Limited" className="h-11 w-auto mx-auto" />
          </Link>
        </div>
        <Card className="bg-white">
          <CardContent className="p-8">
            <h1 className="font-serif text-2xl text-[hsl(var(--brand-navy-2))]">{t('auth.login_title')}</h1>
            <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">{t('auth.login_subtitle')}</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div><Label>{t('auth.email')}</Label><Input required type="email" data-testid="login-email" value={email} onChange={e => setEmail(e.target.value)} /></div>
              <div><Label>{t('auth.password')}</Label><Input required type="password" data-testid="login-password" value={password} onChange={e => setPassword(e.target.value)} /></div>
              <Button type="submit" disabled={busy} data-testid="login-submit-button" className="w-full bg-[hsl(var(--brand-navy))] hover:bg-[hsl(var(--brand-navy-2))]">{t('auth.submit_login')}</Button>
            </form>
            <div className="mt-4 flex justify-between text-sm">
              <Link to="/forgot-password" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">{t('auth.forgot')}</Link>
              <span className="text-[hsl(var(--muted-foreground))]">{t('auth.no_account')} <Link to="/register" data-testid="login-register-link" className="text-[hsl(var(--brand-navy))] font-medium">{t('header.register')}</Link></span>
            </div>
          </CardContent>
        </Card>
        <div className="mt-6 flex justify-center"><LicenseBadge /></div>
      </div>
    </div>
  );
}
